<template>
  <div class="lazy-image-wrapper" :class="{ loaded: isLoaded }">
    <img
      ref="img"
      :data-src="src"
      :alt="alt"
      class="lazy-image"
      :class="{ 'fade-in': isLoaded }"
      @load="onLoad"
    />
    <div v-if="!isLoaded" class="lazy-placeholder">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LazyImage',
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: 'Image'
    }
  },
  data() {
    return {
      isLoaded: false,
      observer: null
    };
  },
  mounted() {
    this.initIntersectionObserver();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage();
            this.observer.unobserve(entry.target);
          }
        });
      }, options);

      this.observer.observe(this.$refs.img);
    },
    loadImage() {
      const img = this.$refs.img;
      if (img && img.dataset.src) {
        img.src = img.dataset.src;
      }
    },
    onLoad() {
      this.isLoaded = true;
      this.$emit('loaded');
    }
  }
};
</script>

<style lang="scss" scoped>
.lazy-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
}

.lazy-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.6s ease-in-out;

  &.fade-in {
    opacity: 1;
  }
}

.lazy-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.dark {
  .lazy-image-wrapper {
    background: rgba(255, 255, 255, 0.05);
  }

  .lazy-placeholder {
    background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
  }

  .spinner {
    border-color: rgba(255, 255, 255, 0.2);
    border-top-color: #fff;
  }
}
</style>
